/**
 * usePendingWorkCounts
 *
 * Reads the PENDING_WORK registry and returns { [lotId]: count } for the lots
 * that declare pending work. Powers the sidebar counter badges.
 *
 * Cheap by construction: `head: true` count queries return a count and NO rows,
 * so this costs one lightweight request per registered lot — currently one. The
 * sidebar mounts on every screen in the app, so this must never become a page of
 * data. If the registry ever grows past a handful of lots, move it behind ONE
 * RPC returning all counts rather than fanning out here.
 *
 * Fails SILENT on purpose. A count is decoration on a nav item — if RLS or the
 * network denies it, the badge simply does not render. It must never break the
 * sidebar, which is the app's primary navigation. Errors still log.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { PENDING_WORK } from '@/constants/pendingWork';

// The queue is human-paced (approvals, reviews), so a slow refresh is plenty and
// keeps the sidebar off the network on every navigation.
const REFRESH_MS = 5 * 60 * 1000;

export function usePendingWorkCounts({ enabled = true } = {}) {
  const [counts, setCounts] = useState({});
  const mountedRef = useRef(true);

  const load = useCallback(async () => {
    const entries = Object.entries(PENDING_WORK);
    if (!entries.length) return;

    const results = await Promise.all(
      entries.map(async ([lotId, cfg]) => {
        try {
          if (cfg.rpc) {
            const { data, error } = await supabase.rpc(cfg.rpc);
            if (error) throw error;
            return [lotId, Number(data) || 0];
          }
          let q = supabase.from(cfg.table).select('id', { count: 'exact', head: true });
          for (const [col, val] of Object.entries(cfg.filters || {})) {
            q = Array.isArray(val) ? q.in(col, val) : q.eq(col, val);
          }
          const { count, error } = await q;
          if (error) throw error;
          return [lotId, count || 0];
        } catch (err) {
          console.warn('[usePendingWorkCounts] count failed', lotId, err.message);
          return [lotId, 0];
        }
      })
    );

    if (!mountedRef.current) return;
    setCounts(Object.fromEntries(results));
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    if (!enabled) return undefined;

    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => {
      mountedRef.current = false;
      clearInterval(timer);
    };
  }, [enabled, load]);

  return { counts, reload: load };
}

export default usePendingWorkCounts;
